import React, { useState, useCallback } from 'react';
import {
    Box,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Toolbar,
    Collapse,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import HomeIcon from '@mui/icons-material/Home';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { storageService } from '../../services/storageService';
import { UserRole } from '../../services/authService';
import { rolePermissions, getUserRolesFromToken } from '../../config/routePermissions';

const NAV_ITEMS = [
    { label: 'Home', icon: <HomeIcon />, path: '/' },
    { label: 'Add', icon: <AddCircleOutlineIcon />, path: '/add' },
    // { label: 'Indigent Search', icon: <SearchIcon />, path: '/search' },
    { label: 'Approval', icon: <TaskAltOutlinedIcon />, path: '/approval' },
    { label: 'Users', icon: <PersonOutlineIcon />, path: '/users' },
];

const REPORT_ITEMS = [
    { label: 'Indigent Register', path: '/reports/indigent-register' },
    { label: 'Dashboard', path: '/reports/dashboard' },
];

const ACTION_ITEMS = [
    { label: 'IT User Action', path: '/it-user-action' },
    { label: 'User Action', path: '/user-action' },
];

const listItemStyles = {
    borderRadius: '12px',
    '&:hover': {
        backgroundColor: '#EEF0FF',
    },
    paddingY: '8px',
    paddingX: '12px',
};

const activeListItemStyles = {
    ...listItemStyles,
    backgroundColor: '#EEF0FF',
    color: '#0A1D56',
};

interface SidebarProps {
    isMobile: boolean;
    isOpen: boolean;
    onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobile, isOpen, onClose }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [openReports, setOpenReports] = useState(false);
    const DRAWER_WIDTH = 240;

    // Get current user roles directly from JWT token for security
    const userRoles = getUserRolesFromToken();

    const isActive = useCallback(
        (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
        [location.pathname],
    );

    // Filter navigation items based on user roles - using centralized functions
    const getFilteredNavItems = () => {
        return rolePermissions.getFilteredNavItems(userRoles, NAV_ITEMS);
    };

    // Filter report items based on user roles - using centralized functions
    const getFilteredReportItems = () => {
        return rolePermissions.getFilteredReportItems(userRoles, REPORT_ITEMS);
    };

    // Filter action items based on user roles - using centralized functions
    const getFilteredActionItems = () => {
        return rolePermissions.getFilteredActionItems(userRoles, ACTION_ITEMS);
    };

    const filteredNavItems = getFilteredNavItems();
    const filteredReportItems = getFilteredReportItems();
    const filteredActionItems = getFilteredActionItems();

    // Show Reports section only if there are report items to show
    const shouldShowReports = filteredReportItems.length > 0;

    const drawerContent = (
        <>
            <Toolbar />
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredNavItems.map(item => (
                    <ListItemButton
                        key={item.label}
                        onClick={() => {
                            navigate(item.path);
                            if (isMobile) onClose(); // close drawer
                        }}
                        sx={isActive(item.path) ? activeListItemStyles : listItemStyles}
                    >
                        <ListItemIcon
                            sx={{
                                color: isActive(item.path) ? '#0A1D56' : '#FF897E',
                                minWidth: '36px',
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography fontWeight={500} fontSize='14px'>
                                    {item.label}
                                </Typography>
                            }
                        />
                    </ListItemButton>
                ))}

                {/* Reports - Only show if there are report items */}
                {shouldShowReports && (
                    <>
                        <ListItemButton
                            onClick={() => setOpenReports(!openReports)}
                            sx={{ ...listItemStyles, color: 'black' }}
                        >
                            <ListItemIcon sx={{ color: '#FF897E', minWidth: '36px' }}>
                                <InsertChartOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography fontWeight={500} fontSize='14px'>
                                        Reports
                                    </Typography>
                                }
                            />
                            {openReports ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>

                        <Collapse in={openReports} timeout='auto' unmountOnExit>
                            <Box sx={{ pl: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {filteredReportItems.map(item => (
                                    <ListItemButton
                                        key={item.label}
                                        onClick={() => {
                                            navigate(item.path);
                                            if (isMobile) onClose();
                                        }}
                                        sx={isActive(item.path) ? activeListItemStyles : listItemStyles}
                                    >
                                        <ListItemText
                                            primary={<Typography fontSize='14px'>{item.label}</Typography>}
                                        />
                                    </ListItemButton>
                                ))}
                            </Box>
                        </Collapse>
                    </>
                )}

                {/* Actions */}
                {filteredActionItems.map(item => (
                    <ListItemButton
                        key={item.label}
                        onClick={() => {
                            navigate(item.path);
                            if (isMobile) onClose();
                        }}
                        sx={{
                            ...listItemStyles,
                            pl: 4,
                            backgroundColor: isActive(item.path) ? '#EEF0FF' : 'transparent',
                        }}
                    >
                        <ListItemText
                            primary={
                                <Typography fontWeight={400} fontSize='14px'>
                                    {item.label}
                                </Typography>
                            }
                        />
                    </ListItemButton>
                ))}
            </List>
        </>
    );

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            anchor='left'
            open={isOpen}
            onClose={onClose}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    paddingTop: '16px',
                    paddingX: '12px',
                    boxShadow: '2px 0 6px rgba(0, 0, 0, 0.08)',
                },
            }}
        >
            {drawerContent}
        </Drawer>
    );
};

export default Sidebar;
